import React, { useCallback, useEffect, useState } from 'react';
import {
    createShippingSizeBand,
    deleteShippingSizeBand,
    getShippingSizeBandsAdmin,
    updateShippingSizeBand,
} from '../../services/api.admin';
import type { ShippingSizeBandAdminDto, UpsertShippingSizeBandDto } from '../../types';

const emptyForm = (): UpsertShippingSizeBandDto => ({
    code: '',
    label: '',
    sortOrder: 10,
    isActive: true,
    buyerAmount: 4.95,
    shippingTitle: '',
    sendcloudShippingMethodId: null,
    nominalWeightGrams: 2000,
    maxWeightGrams: 2000,
    maxLengthCm: null,
    maxWidthCm: null,
    maxHeightCm: null,
    helpText: '',
    allowsPlatformLabel: true,
});

interface ShippingSizeBandSectionProps {
    /**
     * When true, render as content inside a parent "Buyer shipping" card
     * (no outer section shell / duplicate page-level heading).
     */
    embedded?: boolean;
}

/**
 * Admin: package size bands (like platform fee tiers) — buyer shipping amounts + Sendcloud method ids.
 */
const ShippingSizeBandSection: React.FC<ShippingSizeBandSectionProps> = ({ embedded = false }) => {
    const [bands, setBands] = useState<ShippingSizeBandAdminDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<UpsertShippingSizeBandDto>(emptyForm());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            const raw = await getShippingSizeBandsAdmin();
            const list = Array.isArray(raw)
                ? raw
                : (raw as { $values?: ShippingSizeBandAdminDto[] })?.$values ?? [];
            // Client-side dedupe by code (same as server) if API ever returns doubles
            const seen = new Set<string>();
            const unique = list.filter((b) => {
                const key = (b.code ?? '').trim().toUpperCase();
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            setBands(unique);
            return unique;
        } catch {
            setError('Failed to load shipping size bands.');
            setBands([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const startEdit = (b: ShippingSizeBandAdminDto) => {
        setEditingId(b.id);
        setForm({
            code: b.code,
            label: b.label,
            sortOrder: b.sortOrder,
            isActive: b.isActive,
            buyerAmount: Number(b.buyerAmount),
            shippingTitle: b.shippingTitle ?? '',
            sendcloudShippingMethodId: b.sendcloudShippingMethodId ?? null,
            nominalWeightGrams: b.nominalWeightGrams,
            maxWeightGrams: b.maxWeightGrams ?? null,
            maxLengthCm: b.maxLengthCm ?? null,
            maxWidthCm: b.maxWidthCm ?? null,
            maxHeightCm: b.maxHeightCm ?? null,
            helpText: b.helpText ?? '',
            allowsPlatformLabel: b.allowsPlatformLabel,
        });
    };

    const startNew = () => {
        setEditingId(null);
        const nextOrder = bands.length > 0 ? Math.max(...bands.map((b) => b.sortOrder)) + 1 : 1;
        setForm({ ...emptyForm(), sortOrder: nextOrder });
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload: UpsertShippingSizeBandDto = {
                ...form,
                code: form.code.trim().toUpperCase(),
                label: form.label.trim(),
                shippingTitle: form.shippingTitle?.trim() || null,
                helpText: form.helpText?.trim() || null,
                sendcloudShippingMethodId:
                    form.sendcloudShippingMethodId && form.sendcloudShippingMethodId > 0
                        ? form.sendcloudShippingMethodId
                        : null,
            };
            if (editingId != null) {
                await updateShippingSizeBand(editingId, payload);
            } else {
                await createShippingSizeBand(payload);
            }
            await load();
            startNew();
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { errors?: { General?: string } } } })?.response?.data
                    ?.errors?.General ?? 'Save failed.';
            setError(String(msg));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        if (!window.confirm('Delete this size band? Prefer deactivating if products still use it.'))
            return;
        setError(null);
        try {
            await deleteShippingSizeBand(id);
            await load();
            if (editingId === id) startNew();
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { errors?: { General?: string } } } })?.response?.data
                    ?.errors?.General ?? 'Delete failed.';
            setError(String(msg));
        }
    };

    const body = (
        <>
            {!embedded && (
                <>
                    <h2 className="mb-2 text-xl font-bold text-gray-800">Package size bands (shipping)</h2>
                    <p className="mb-4 text-sm text-gray-500">
                        Like platform fee tiers: add as many sizes as you need. Each band has a buyer
                        shipping fee and optional Sendcloud method id for labels. Sellers pick a band
                        when listing (mandatory). Set category defaults under Categories.
                    </p>
                </>
            )}
            {embedded && (
                <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Package size bands</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Primary buyer shipping amounts and Sendcloud method per size (S/M/L/XL or custom).
                        Sellers must pick a band when listing. Category defaults are set under Categories.
                    </p>
                </div>
            )}

            {error && (
                <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="mb-6 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                    <thead>
                        <tr className="border-b text-gray-600">
                            <th className="py-2 pr-3">Order</th>
                            <th className="py-2 pr-3">Code</th>
                            <th className="py-2 pr-3">Label</th>
                            <th className="py-2 pr-3">Buyer €</th>
                            <th className="py-2 pr-3">Weight g</th>
                            <th className="py-2 pr-3">Sendcloud method</th>
                            <th className="py-2 pr-3">Active</th>
                            <th className="py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bands.map((b) => (
                            <tr key={b.id} className="border-b border-gray-100">
                                <td className="py-2 pr-3">{b.sortOrder}</td>
                                <td className="py-2 pr-3 font-mono">{b.code}</td>
                                <td className="py-2 pr-3">{b.label}</td>
                                <td className="py-2 pr-3">{Number(b.buyerAmount).toFixed(2)}</td>
                                <td className="py-2 pr-3">{b.nominalWeightGrams}</td>
                                <td className="py-2 pr-3">{b.sendcloudShippingMethodId ?? '—'}</td>
                                <td className="py-2 pr-3">{b.isActive ? 'Yes' : 'No'}</td>
                                <td className="space-x-2 py-2">
                                    <button
                                        type="button"
                                        className="text-indigo-600 hover:underline"
                                        onClick={() => startEdit(b)}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        className="text-red-600 hover:underline"
                                        onClick={() => void remove(b.id)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {bands.length === 0 && (
                            <tr>
                                <td colSpan={8} className="py-4 text-gray-500">
                                    No bands yet. Seed SQL should create S/M/L/XL, or add manually.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mb-2 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                    {editingId != null ? `Edit band #${editingId}` : 'Add band'}
                </h3>
                {editingId != null && (
                    <button type="button" className="text-sm text-gray-600 hover:underline" onClick={startNew}>
                        Cancel edit
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Code</label>
                    <input
                        className="w-full rounded border px-3 py-2 font-mono uppercase"
                        value={form.code}
                        onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                        placeholder="S"
                        maxLength={20}
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Label</label>
                    <input
                        className="w-full rounded border px-3 py-2"
                        value={form.label}
                        onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                        placeholder="Small"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Sort order</label>
                    <input
                        type="number"
                        className="w-full rounded border px-3 py-2"
                        value={form.sortOrder}
                        onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Buyer amount (€)</label>
                    <input
                        type="number"
                        step="0.01"
                        min={0}
                        className="w-full rounded border px-3 py-2"
                        value={form.buyerAmount}
                        onChange={(e) => setForm((f) => ({ ...f, buyerAmount: Number(e.target.value) }))}
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Nominal weight (g)</label>
                    <input
                        type="number"
                        min={1}
                        className="w-full rounded border px-3 py-2"
                        value={form.nominalWeightGrams}
                        onChange={(e) =>
                            setForm((f) => ({ ...f, nominalWeightGrams: Number(e.target.value) }))
                        }
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                        Sendcloud method id
                    </label>
                    <input
                        type="number"
                        className="w-full rounded border px-3 py-2"
                        value={form.sendcloudShippingMethodId ?? ''}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                sendcloudShippingMethodId: e.target.value
                                    ? Number(e.target.value)
                                    : null,
                            }))
                        }
                        placeholder="From Sendcloud panel"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-gray-600">Help text (sellers)</label>
                    <input
                        className="w-full rounded border px-3 py-2"
                        value={form.helpText ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, helpText: e.target.value }))}
                        placeholder="Clothes, small soft toys…"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Max weight g (display)</label>
                    <input
                        type="number"
                        className="w-full rounded border px-3 py-2"
                        value={form.maxWeightGrams ?? ''}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                maxWeightGrams: e.target.value ? Number(e.target.value) : null,
                            }))
                        }
                    />
                </div>
                <div className="flex items-center gap-4 md:col-span-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={form.isActive}
                            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                        />
                        Active
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={form.allowsPlatformLabel}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, allowsPlatformLabel: e.target.checked }))
                            }
                        />
                        Allows platform Bpost label
                    </label>
                </div>
            </div>

            <div className="mt-4">
                <button
                    type="button"
                    disabled={saving}
                    onClick={() => void save()}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                    {saving ? 'Saving…' : editingId != null ? 'Update band' : 'Add band'}
                </button>
            </div>
        </>
    );

    if (loading) {
        if (embedded) {
            return <p className="text-sm text-gray-600">Loading shipping size bands…</p>;
        }
        return (
            <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <p className="text-gray-600">Loading shipping size bands…</p>
            </section>
        );
    }

    if (embedded) {
        return <div className="mt-2 border-t border-gray-200 pt-6">{body}</div>;
    }

    return (
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">{body}</section>
    );
};

export default ShippingSizeBandSection;
